import { useEffect, useMemo } from "react";

import {
  AppBar,
  CircularProgressCenter,
  Drawer,
  FireCMS,
  FireCMSi18nProvider,
  ModeControllerProvider,
  NavigationRoutes,
  Scaffold,
  SideDialogs,
  SnackbarProvider,
  useBuildLocalConfigurationPersistence,
  useBuildModeController,
  useBuildNavigationController,
  useTranslation,
  useValidateAuthenticator,
} from "@firecms/core";
import {
  useFirebaseAuthController,
  useFirebaseStorageSource,
  useFirestoreDelegate,
  useInitialiseFirebase,
  type FirebaseAuthController,
  type FirebaseSignInProvider,
} from "@firecms/firebase";
import { CenteredView } from "@firecms/ui";
import logoUrl from "./assets/logo.svg";

import { firebaseConfig, isStagingFirebase } from "./firebase_config";
import { authenticator } from "./utils/authenticator";
import {
  getAppBarTitle,
  useAdminWorkspace,
} from "./workspace/useAdminWorkspace";
import { collectionsBuilderCreator } from "./collections/builder";
import {
  buildAccountPath,
  getCmsBasePathUrl,
} from "./workspace/workspaceRoutes";
import {
  getEventAccessDeniedTitle,
  getEventManagerAccessDeniedMessage,
  getEventProvisioningDeniedTitle,
  getPanelAccessBackLabel,
} from "./workspace/eventAccessMessages";
import { getAdminUiLocale } from "./i18n/getAdminUiLocale";
import { AdminLanguageSelector } from "./components/AdminLanguageSelector";
import { AdminBuildVersion } from "./components/AdminBuildVersion";
import { WorkspaceTitleSync } from "./workspace/WorkspaceTitleSync";
import { LearningPathBackControl } from "./workspace/LearningPathBackControl";
import { EventManagementBackControl } from "./workspace/EventManagementBackControl";
import { AuthBootGate } from "./components/AuthBootGate";
import { AdminBootSplash } from "./components/AdminBootSplash";
import { usePanelExtras } from "./hooks/usePanelExtras";
import { PanelAccessDenied } from "./components/PanelAccessDenied";
import {
  FirecmsLocaleBootstrap,
  HideFirecmsLanguageToggle,
} from "./components/FirecmsLocaleBootstrap";
import { normalizeFireCMSLocale, sanitizeStoredFireCMSLocale } from "./i18n/adminLocales";
import { ChangeSlotTeamDialogHost } from "./actions/changeSlotTeam/ChangeSlotTeamDialogHost";
import { ApproveStandbyDialogHost } from "./actions/approveStandby/ApproveStandbyDialogHost";
import { ParticipantEconomyDialogHost } from "./actions/participantEconomy/ParticipantEconomyDialogHost";
import { AccountInvitationPage } from "./views/AccountInvitationPage";
import { AdminHub } from "./views/AdminHub";
import { registerWorkspaceNavigation } from "./actions/workspaceNavigationBridge";

function App() {
  const initialLocale = sanitizeStoredFireCMSLocale();
  const workspace = useAdminWorkspace();

  useEffect(
    () =>
      registerWorkspaceNavigation({
        openSpace: workspace.openSpace,
        openEvent: workspace.openEvent,
      }),
    [workspace.openSpace, workspace.openEvent]
  );

  const cmsBasePath = useMemo(
    () => getCmsBasePathUrl(workspace.mode),
    [workspace.workspaceKey]
  );

  const {
    firebaseApp,
    firebaseConfigLoading,
    configError
  } = useInitialiseFirebase({
    firebaseConfig
  });

  const modeController = useBuildModeController();

  const signInOptions: FirebaseSignInProvider[] = isStagingFirebase
    ? ["password"]
    : ["google.com", "password"];

  const authController: FirebaseAuthController = useFirebaseAuthController({
    firebaseApp,
    signInOptions
  });

  const userConfigPersistence = useBuildLocalConfigurationPersistence();

  const firestoreDelegate = useFirestoreDelegate({
    firebaseApp,
  });

  const storageSource = useFirebaseStorageSource({
    firebaseApp
  });

  const {
    authLoading,
    canAccessMainView,
    notAllowedError
  } = useValidateAuthenticator({
    authController,
    authenticator: authenticator,
    dataSourceDelegate: firestoreDelegate,
    storageSource
  });

  if (firebaseConfigLoading || !firebaseApp) {
    return <>
      <CircularProgressCenter />
    </>;
  }

  if (configError) {
    return <CenteredView>{configError}</CenteredView>;
  }

  return (
    <FireCMSi18nProvider locale={initialLocale}>
      <FirecmsLocaleBootstrap />
      <HideFirecmsLanguageToggle />
      <SnackbarProvider>
        <ModeControllerProvider value={modeController}>
          <FireCMSRoot
            cmsBasePath={cmsBasePath}
            workspace={workspace}
            authLoading={authLoading}
            authController={authController}
            userConfigPersistence={userConfigPersistence}
            firestoreDelegate={firestoreDelegate}
            storageSource={storageSource}
            canAccessMainView={canAccessMainView}
            firebaseApp={firebaseApp}
            signInOptions={signInOptions}
            notAllowedError={notAllowedError}
            logoUrl={logoUrl}
          />
        </ModeControllerProvider>
      </SnackbarProvider>
    </FireCMSi18nProvider>
  );

}

type WorkspaceAppProps = {
  workspace: ReturnType<typeof useAdminWorkspace>;
  logoUrl: string;
};

type FireCMSRootProps = WorkspaceAppProps & {
  cmsBasePath: string;
  authLoading: boolean;
  authController: FirebaseAuthController;
  userConfigPersistence: ReturnType<typeof useBuildLocalConfigurationPersistence>;
  firestoreDelegate: ReturnType<typeof useFirestoreDelegate>;
  storageSource: ReturnType<typeof useFirebaseStorageSource>;
  canAccessMainView: boolean;
  firebaseApp: NonNullable<ReturnType<typeof useInitialiseFirebase>["firebaseApp"]>;
  signInOptions: FirebaseSignInProvider[];
  notAllowedError?: string;
};

function FireCMSRoot({
  cmsBasePath,
  workspace,
  authLoading,
  authController,
  userConfigPersistence,
  firestoreDelegate,
  storageSource,
  canAccessMainView,
  firebaseApp,
  signInOptions,
  notAllowedError,
  logoUrl,
}: FireCMSRootProps) {
  const { i18n } = useTranslation();
  const adminLocale = normalizeFireCMSLocale(i18n.language);

  const collectionsBuilder = useMemo(
    () => collectionsBuilderCreator(workspace.mode, adminLocale),
    [workspace.workspaceKey, adminLocale]
  );

  const navigationController = useBuildNavigationController({
    basePath: cmsBasePath,
    baseCollectionPath: "c",
    disabled: authLoading,
    collections: collectionsBuilder,
    authController,
    dataSourceDelegate: firestoreDelegate,
    views: workspace.buildCMSViews,
  });

  return (
    <FireCMS
      key={cmsBasePath}
      navigationController={navigationController}
      authController={authController}
      userConfigPersistence={userConfigPersistence}
      dataSourceDelegate={firestoreDelegate}
      storageSource={storageSource}
    >
      {({ loading }) => (
        <FireCMSAppContent
          loading={loading}
          authLoading={authLoading}
          canAccessMainView={canAccessMainView}
          authController={authController}
          firebaseApp={firebaseApp}
          signInOptions={signInOptions}
          notAllowedError={notAllowedError}
          workspace={workspace}
          logoUrl={logoUrl}
        />
      )}
    </FireCMS>
  );
}

type FireCMSAppContentProps = WorkspaceAppProps & {
  loading: boolean;
  authLoading: boolean;
  canAccessMainView: boolean;
  authController: FirebaseAuthController;
  firebaseApp: NonNullable<ReturnType<typeof useInitialiseFirebase>["firebaseApp"]>;
  signInOptions: FirebaseSignInProvider[];
  notAllowedError?: string;
};

function FireCMSAppContent({
  loading,
  authLoading,
  canAccessMainView,
  authController,
  firebaseApp,
  signInOptions,
  notAllowedError,
  workspace,
  logoUrl,
}: FireCMSAppContentProps) {
  return (
    <AuthBootGate
      authLoading={authLoading}
      canAccessMainView={canAccessMainView}
      authController={authController}
      firebaseApp={firebaseApp}
      signInOptions={signInOptions}
      notAllowedError={notAllowedError}
      loading={loading}
    >
      <FireCMSAppBody workspace={workspace} logoUrl={logoUrl} />
    </AuthBootGate>
  );
}

function FireCMSAppBody({ workspace, logoUrl }: WorkspaceAppProps) {
  const {
    loading: panelExtrasLoading,
    accessDenied,
    denialMessage,
    denialReason,
  } = usePanelExtras(workspace.mode);

  if (panelExtrasLoading) {
    return <AdminBootSplash />;
  }

  if (accessDenied) {
    const locale = getAdminUiLocale();
    const isEventMode = workspace.mode.kind === "event";
    const isProvisioningDenial = denialReason === "provisioning";
    const isEventAccessDenial = denialReason === "event_access";

    return (
      <PanelAccessDenied
        title={
          isEventMode && isProvisioningDenial
            ? getEventProvisioningDeniedTitle(locale)
            : isEventMode && isEventAccessDenial
              ? getEventAccessDeniedTitle(locale)
              : undefined
        }
        message={
          isEventAccessDenial
            ? getEventManagerAccessDeniedMessage(locale)
            : denialMessage
        }
        backLabel={
          isEventMode && workspace.selectedAccountId
            ? getPanelAccessBackLabel(locale)
            : undefined
        }
        backHref={
          isEventMode && workspace.selectedAccountId
            ? buildAccountPath(workspace.selectedAccountId)
            : undefined
        }
      />
    );
  }

  return (
    <>
      <ChangeSlotTeamDialogHost />
      <ApproveStandbyDialogHost />
      <ParticipantEconomyDialogHost />
      <WorkspaceTitleSync
        mode={workspace.mode}
        selectedAccountId={workspace.selectedAccountId}
        selectedSpaceId={workspace.selectedSpaceId}
        selectedEventId={workspace.selectedEventId}
        setAccountTitle={workspace.setAccountTitle}
        setTitle={workspace.setTitle}
        setSpaceTitle={workspace.setSpaceTitle}
        setHydratedSpaceId={workspace.setHydratedSpaceId}
      />
      <WorkspaceAppBar workspace={workspace} logoUrl={logoUrl} />
    </>
  );
}

function WorkspaceAppBar({ workspace, logoUrl }: WorkspaceAppProps) {
  const appBarTitle = getAppBarTitle(workspace);

  const showEventBack =
    workspace.mode.kind === "event" && !!workspace.selectedAccountId;

  if (workspace.mode.kind === "hub") {
    return (
      <Scaffold autoOpenDrawer={false} logo={logoUrl}>
        <AppBar
          title={appBarTitle}
          endAdornment={
            <>
              <AdminBuildVersion />
              <AdminLanguageSelector />
            </>
          }
        />
        {workspace.isInvite ? <AccountInvitationPage /> : <AdminHub />}
        <SideDialogs />
      </Scaffold>
    );
  }

  return (
    <Scaffold autoOpenDrawer={false} logo={logoUrl}>
      <AppBar
        title={appBarTitle}
        endAdornment={
          <>
            {workspace.mode.kind === "space" ? (
              <LearningPathBackControl onBack={workspace.clearToAccount} />
            ) : null}
            {showEventBack ? (
              <EventManagementBackControl onBack={workspace.clearToAccount} />
            ) : null}
            <AdminBuildVersion />
            <AdminLanguageSelector />
          </>
        }
      />
      <Drawer />
      <NavigationRoutes />
      <SideDialogs />
    </Scaffold>
  );
}

export default App;
